import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import _isPlainObject from 'lodash/isPlainObject'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import { useCookies } from 'react-cookie'

import PageLoader from '~/components/ui/PageLoader'
import { ErrorComp } from '~/components/ui/sharedStyledComponents'
import { LUMOSITY_USER_COOKIE, UserHelpCenterUrl } from '~/constants'
import { useRegionalContext } from '~/context/RegionalContext'
import { HandShakeContextType } from '~/events/eventTypes'
import useTrackEvents from '~/events/trackers/useTrackEvents'
import { useEventsInfo } from '~/events/useEventsInfo'
import logger from '~/utils/logger'
import { OBSubscriptionDataResponse } from '~/utils/planUtils'

const { publicRuntimeConfig } = getConfig()
const obOrigin = publicRuntimeConfig?.optimizeBox?.origin
const obIframePath = publicRuntimeConfig?.optimizeBox?.iframePath
const obIframePathUSA = publicRuntimeConfig?.optimizeBox?.iframePathUSA
const enableOBFromEnv = publicRuntimeConfig?.optimizeBox?.enable

export const OBLoader = ({ L2Comp, OBComp }: { L2Comp: React.ReactNode; OBComp: React.ReactNode }) => {
  const [loading, setLoading] = useState(true)
  const [features, setFeatures] = useState<{ enableOB: boolean } | null>(null)
  const { lang } = useTranslation()
  const userLanguage = lang || 'en'

  useEffect(() => {
    async function getFeatures() {
      try {
        // TODO : remove this once we have a async source for features
        // const response = await fetch('/features.json') // TODO : get the feature from some async source
        // const data = await response.json()
        setFeatures({ enableOB: true })
      } catch (error) {
        logger.error('L2-OBLoader::getFeatures::error', error)
      } finally {
        setLoading(false)
      }
    }

    getFeatures()
  }, [])

  if (loading) {
    return <PageLoader />
  }
  if (!enableOBFromEnv) {
    return L2Comp
  }
  if (!features?.enableOB) {
    return L2Comp
  }
  if (userLanguage !== 'en') {
    return L2Comp
  }
  return OBComp
}

type SendMessageFunction = (messageData: any) => void
type ReceiveMessageFunction = (e: MessageEvent) => void
interface OBIframeProps {
  onReceiveMessage: (e: MessageEvent, sendMessage: SendMessageFunction) => void
  handShakeContext: HandShakeContextType
  onIframeError?: () => void
  style?: React.CSSProperties
}

const OBIframe = ({ onReceiveMessage, handShakeContext, onIframeError, style }: OBIframeProps) => {
  const { countryCode } = useRegionalContext()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  const obIframeUrl = useMemo(() => {
    const obIframePathOverride = (() => {
      if (typeof localStorage === 'undefined') {
        return ''
      }
      return localStorage.getItem('ob_iframe_path_override') || ''
    })()

    const obBasePath = (() => {
      if (obIframePathOverride) {
        return obIframePathOverride
      }
      if (countryCode?.toUpperCase() === 'US') {
        return obIframePathUSA
      }
      return obIframePath
    })()

    const _iframePath = (() => {
      if (handShakeContext === 'subscription') {
        return `${obBasePath}/subscription`
      }
      return obBasePath
    })()

    const urlString = `${obOrigin}${_iframePath}`
    const url = new URL(urlString)
    url.searchParams.set('l2_context', handShakeContext)
    url.searchParams.set('country_code', countryCode)
    url.searchParams.set('top_level_href', window.btoa(encodeURIComponent(window.location.href)))
    return url.toString()
  }, [handShakeContext, countryCode])

  useLayoutEffect(() => {
    const sendMessage: SendMessageFunction = (messageData) => {
      iframeRef?.current?.contentWindow?.postMessage?.(messageData, obOrigin)
      logger.debug('L2-Send-to-OB', messageData)
    }

    const receiveMessage: ReceiveMessageFunction = (e) => {
      if (e.origin !== obOrigin) return
      logger.debug('L2-Receive-from-OB', e.data)
      onReceiveMessage(e, sendMessage)
    }

    window.addEventListener('message', receiveMessage)
    return () => {
      window.removeEventListener('message', receiveMessage)
    }
  }, [onReceiveMessage])

  const _onIframeError = useCallback(
    (e: any) => {
      /**
       * Source: ChatGPT
       * Browsers treat <iframe> differently from <img> or <script>, and they do not emit reliable onError events for iframes when the destination is unreachable, especially for:
          DNS resolution failures
          Network errors
          Security blocks
          chrome-error://chromewebdata/ cases
       */
      setLoading(false)
      logger.error('L2-OBIframe::iframeError', e, obIframeUrl)
      setError(e)
      onIframeError?.()
    },
    [obIframeUrl, onIframeError],
  )

  const onIframeLoad = useCallback(() => {
    setLoading(false)
  }, [])

  return (
    <>
      {loading ? <PageLoader /> : null}
      {error ? <ErrorComp /> : null}
      <iframe
        ref={iframeRef}
        title='ob'
        src={obIframeUrl}
        className='w-full h-[100dvh] overflow-auto'
        onError={_onIframeError}
        onLoad={onIframeLoad}
        data-hj-allow-iframe='true'
        style={{ ...style }}
      ></iframe>
    </>
  )
}

export type OBFitTestResultsType = {
  gameSlug: GameSlug
  gameLabel: string
  areaSlug: AreaSlug
  areaLabel: string
  percentile: number
  score: number
}[]

type OBCommonFrameProps = {
  handShakeContext: HandShakeContextType
  fitTestResults?: OBFitTestResultsType
  onSubscriptionData?: (data: { planSetName: string }) => Promise<OBSubscriptionDataResponse | undefined>
  onIframeError?: () => void
  style?: React.CSSProperties
}

export const OBCommonFrame = ({
  handShakeContext,
  fitTestResults,
  onSubscriptionData,
  onIframeError,
  style,
}: OBCommonFrameProps) => {
  const { getEventInfo } = useEventsInfo()
  const router = useRouter()

  // because OB can send message saying OPEN_<PageName>_PAGE, L2 code will route the user to the PageName
  // because router is involved, it takes some time for the redirection to happen,
  // during which user might feel stuck if there is a delay in response, thus we will open a loader whenever we are about to redirect
  // RISK - we need to carefully check if we need to make loader off, for any error cases
  const [loading, setLoading] = useState(false)
  const [{ [LUMOSITY_USER_COOKIE]: lumosityUserCookie }] = useCookies([LUMOSITY_USER_COOKIE])
  const { trackEvent } = useTrackEvents()

  const onReceiveMessage = useCallback(
    (e: MessageEvent, sendMessage: SendMessageFunction) => {
      const payload = e?.data?.payload && _isPlainObject(e?.data?.payload) ? e.data.payload : undefined

      const Actions = {
        OB_READY: () => {
          sendMessage?.({
            action: 'L2_INIT',
            payload: {
              context: handShakeContext,
              // send partial data, OB should be populating/overriding dynamic info
              partialEventInfo: getEventInfo(),
              lumosityUserCookie: lumosityUserCookie,
              ...(fitTestResults ? { fitTestResults } : {}),
            },
          })
        },
        OPEN_HOME_PAGE: () => {
          setLoading(true)
          router.push('/')
        },
        OPEN_REGISTRATION_PAGE: () => {
          setLoading(true)
          router.push('/signup')
        },
        OPEN_LOGIN_PAGE: () => {
          setLoading(true)
          router.push('/login')
        },
        OPEN_FIT_TEST_PAGE: () => {
          setLoading(true)
          // // sample payload
          // payload : {
          //   fitVariant: 'result_post_fit'
          //   gameSlugs : ['color-match', 'memory-matrix']
          //   isOBDone: false
          // }
          if (!payload) {
            logger.error('L2-OBCommonFrame::OPEN_FIT_TEST_PAGE::payload is undefined')
            return
          }
          localStorage.setItem('ob_action__open_fit_test_page', JSON.stringify(payload))
          router.push('/fit-test')
        },
        OPEN_PRIVACY_POLICY: () => {
          setLoading(true)
          router.push('/privacy-policy')
        },
        OPEN_TOS: () => {
          setLoading(true)
          router.push('/terms-of-service')
        },
        OPEN_CALIFORNIA_PRIVACY: () => {
          setLoading(true)
          router.push('/privacy-policy#what-information-we-collect')
        },
        OPEN_HELP_CENTER: () => {
          setLoading(true)
          window.open(UserHelpCenterUrl, '_self')
        },
        OPEN_SETTINGS: () => {
          setLoading(true)
          router.push('/settings')
        },
        OPEN_LOGOUT_PAGE: () => {
          router.push('/logout')
        },
        OPEN_PLAN_SELECTION_PAGE: () => {
          setLoading(true)
          router.push('/subscribe')
        },
        ON_SUBSCRIPTION_DATA: async () => {
          // // sample payload
          // payload : {
          //   planSetName: 'nup1',
          // }
          if (!payload) {
            logger.error('L2-OBCommonFrame::ON_SUBSCRIPTION_DATA::payload is undefined')
            return
          }
          if (!onSubscriptionData) {
            logger.error('L2-OBCommonFrame::ON_SUBSCRIPTION_DATA::onSubscriptionData was not passed')
            return
          }
          const response = await onSubscriptionData(payload)
          sendMessage?.({
            action: 'SUBSCRIPTION_PAGE_DATA',
            payload: response,
          })
        },
        OPEN_CHECKOUT_PAGE: () => {
          setLoading(true)
          if (!payload) {
            logger.error('L2-OBCommonFrame::OPEN_CHECKOUT_PAGE::payload is undefined')
            return
          }
          router.push(`/checkout?sku=${payload.sku}`)
        },
      } as const

      const action: string = e.data?.action || ''
      Actions[action as keyof typeof Actions]?.()
    },
    [handShakeContext, getEventInfo, lumosityUserCookie, fitTestResults, router, onSubscriptionData],
  )

  useEffect(() => {
    trackEvent('ob_iframe_launch', { context: handShakeContext })
  }, [trackEvent, handShakeContext])

  return (
    <>
      {loading ? <PageLoader /> : null}
      <OBIframe
        onReceiveMessage={onReceiveMessage}
        handShakeContext={handShakeContext}
        onIframeError={onIframeError}
        style={style}
      />
    </>
  )
}
